/**
 * Hook para dados de série temporal do Dashboard Admin
 * Utiliza RPC para agregação no banco, contornando limite de 1000 registros do Supabase
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { subDays, format, startOfDay } from 'date-fns';

// =====================================================
// Interfaces
// =====================================================

interface TenantsByDay {
  date: string;
  count: number;
  cumulative: number;
}

interface MessagesByDay {
  date: string;
  count: number;
}

interface UsersByDay {
  date: string;
  count: number;
  cumulative: number;
}

interface AdminTimeSeriesData {
  tenantsByDay: TenantsByDay[];
  messagesByDay: MessagesByDay[];
  usersByDay: UsersByDay[];
  totalTenants: number;
  totalUsers: number;
  totalMessages: number;
}

// =====================================================
// Hook Principal
// =====================================================

export function useAdminDashboardTimeSeries(days: number = 30) {
  return useQuery({
    queryKey: ['admin-dashboard-timeseries', days],
    queryFn: async (): Promise<AdminTimeSeriesData> => {
      const startDate = startOfDay(subDays(new Date(), days - 1));

      const [
        tenantsResult,
        usersResult,
        totalTenantsResult,
        totalUsersResult,
        // RPC para mensagens por dia (agregação no banco)
        messagesByDayResult,
      ] = await Promise.all([
        // Tenants criados no período
        supabase
          .from('tenants')
          .select('id, created_at')
          .gte('created_at', startDate.toISOString())
          .order('created_at', { ascending: true }),
        
        // Usuários criados no período
        supabase
          .from('profiles')
          .select('id, created_at')
          .gte('created_at', startDate.toISOString())
          .order('created_at', { ascending: true }),
        
        // Contagem total para cálculo cumulativo
        supabase
          .from('tenants')
          .select('id', { count: 'exact', head: true })
          .lt('created_at', startDate.toISOString()),
        
        supabase
          .from('profiles')
          .select('id', { count: 'exact', head: true })
          .lt('created_at', startDate.toISOString()),
        
        // RPC para mensagens por dia (sem limite de 1000)
        supabase.rpc('get_admin_messages_by_day', { days_ago: days }),
      ]);

      const tenants = tenantsResult.data || [];
      const users = usersResult.data || [];
      const messagesRpcData = messagesByDayResult.data || [];

      // Inicializar mapas para cada dia
      const tenantsByDayMap = new Map<string, number>();
      const usersByDayMap = new Map<string, number>();
      const messagesByDayMap = new Map<string, number>();

      for (let i = 0; i < days; i++) {
        const date = format(subDays(new Date(), days - 1 - i), 'yyyy-MM-dd');
        tenantsByDayMap.set(date, 0);
        usersByDayMap.set(date, 0);
        messagesByDayMap.set(date, 0);
      }

      // Contar tenants por dia
      tenants.forEach((tenant: { created_at: string }) => {
        const date = format(new Date(tenant.created_at), 'yyyy-MM-dd');
        if (tenantsByDayMap.has(date)) {
          tenantsByDayMap.set(date, (tenantsByDayMap.get(date) || 0) + 1);
        }
      });

      // Contar usuários por dia
      users.forEach((user: { created_at: string }) => {
        const date = format(new Date(user.created_at), 'yyyy-MM-dd');
        if (usersByDayMap.has(date)) {
          usersByDayMap.set(date, (usersByDayMap.get(date) || 0) + 1);
        }
      });

      // Mapear mensagens por dia (via RPC)
      let totalMessagesFromRpc = 0;
      messagesRpcData.forEach((item: { day_date: string; message_count: number }) => {
        const date = item.day_date;
        const count = Number(item.message_count);
        if (messagesByDayMap.has(date)) {
          messagesByDayMap.set(date, count);
        }
        totalMessagesFromRpc += count;
      });

      // Construir arrays com contagens cumulativas
      let tenantsCumulative = totalTenantsResult.count || 0;
      const tenantsByDay: TenantsByDay[] = Array.from(tenantsByDayMap.entries()).map(([date, count]) => {
        tenantsCumulative += count;
        return { date, count, cumulative: tenantsCumulative };
      });

      let usersCumulative = totalUsersResult.count || 0;
      const usersByDay: UsersByDay[] = Array.from(usersByDayMap.entries()).map(([date, count]) => {
        usersCumulative += count;
        return { date, count, cumulative: usersCumulative };
      });

      const messagesByDay: MessagesByDay[] = Array.from(messagesByDayMap.entries()).map(([date, count]) => ({
        date,
        count,
      }));

      return {
        tenantsByDay,
        messagesByDay,
        usersByDay,
        totalTenants: tenantsCumulative,
        totalUsers: usersCumulative,
        totalMessages: totalMessagesFromRpc,
      };
    },
    staleTime: 1000 * 60 * 5,
  });
}
